import { isOffPlatformNoticeKind, isSystemKind } from '#modules/chat/utils/systemMessage';
import { gqlFetcher, graphql } from '#features/api';
import { notePushWorthyAction } from '#features/notifications/utils/pushPrompt';
import { isDefined } from '#utils/isDefined';

const chatsDoc = graphql(`
  query Chats($offset: Int, $limit: Int, $kind: ChatKind) {
    chat {
      list(offset: $offset, limit: $limit, kind: $kind) {
        chats {
          id
          kind
          sessionId
          postId
          gameId
          sessionStartedAt
          sessionExpiredAt
          postDeletedAt
          host {
            userId
            userData {
              id
              displayName
            }
          }
          memberIds
          members {
            userId
            userData {
              id
              displayName
              avatar
            }
          }
          muted
          closedAt
          readOnly
          userId
          userOneId
          userTwoId
          lastMessageId
          lastMessageAt
          lastMessageText
          unreadCount
          updatedAt
          userData {
            id
            displayName
            avatar
            lastVisit
            blockState
            deleted
          }
        }
        hasMore
        totalCount
      }
    }
  }
`);

type ChatsQueryResult = {
  chat?: {
    list?: {
      chats?: Array<{
        id?: string | null;
        kind?: string | null;
        sessionId?: string | null;
        postId?: string | null;
        gameId?: string | null;
        sessionStartedAt?: string | null;
        sessionExpiredAt?: string | null;
        postDeletedAt?: string | null;
        host?: {
          userId?: string | null;
          userData?: {
            id?: string | null;
            displayName?: string | null;
          } | null;
        } | null;
        memberIds?: Array<string | null> | null;
        members?: Array<{
          userId?: string | null;
          userData?: {
            id?: string | null;
            displayName?: string | null;
            avatar?: string | null;
          } | null;
        } | null> | null;
        muted?: boolean | null;
        closedAt?: string | null;
        readOnly?: boolean | null;
        userId?: string | null;
        userOneId?: string | null;
        userTwoId?: string | null;
        lastMessageId?: string | null;
        lastMessageAt?: string | null;
        lastMessageText?: string | null;
        unreadCount?: unknown;
        updatedAt?: string | null;
        userData?: {
          id?: string | null;
          displayName?: string | null;
          avatar?: string | null;
          lastVisit?: string | null;
          blockState?: 'TEMPORARY' | 'PERMANENT' | null;
          deleted?: boolean | null;
        } | null;
      } | null> | null;
      hasMore?: boolean | null;
      totalCount?: number | null;
    } | null;
  } | null;
};

const mapChatsResult = (data: ChatsQueryResult) => ({
  chats:
    data?.chat?.list?.chats?.filter(isDefined).map((chat) => ({
      ...chat,
      unreadCount: Number(chat.unreadCount ?? 0),
    })) ?? [],
  hasMore: data?.chat?.list?.hasMore ?? false,
  totalCount: data?.chat?.list?.totalCount ?? 0,
});

export const chatsQueryOptions = (input: {
  offset: number;
  limit: number;
  kind?: ChatKind | null;
}) => ({
  queryKey: ['chats', input.limit, input.offset, input.kind ?? 'ALL'],
  queryFn: () =>
    gqlFetcher(chatsDoc, input).then((data) => mapChatsResult(data as ChatsQueryResult)),
});

/** Вид переписки для вкладок над списком. Пусто — все виды. */
export type ChatKind = 'DIRECT' | 'SESSION';

const chatCountsDoc = graphql(`
  query ChatCounts {
    chat {
      counts {
        all
        direct
        session
        unreadAll
        unreadDirect
        unreadSession
      }
    }
  }
`);

export type ChatCounts = {
  all: number;
  direct: number;
  session: number;
  unreadAll: number;
  unreadDirect: number;
  unreadSession: number;
};

const EMPTY_COUNTS: ChatCounts = {
  all: 0,
  direct: 0,
  session: 0,
  unreadAll: 0,
  unreadDirect: 0,
  unreadSession: 0,
};

/*
  Счётчики спрашиваются отдельно от страницы списка. Список подгружается по двадцать строк, а
  вкладки обязаны знать про все переписки: считать по видимой странице значит показать «Сессии 0»
  тому, у кого чат сессии просто не поместился в первые двадцать.
*/
export const chatCountsQueryOptions = () => ({
  queryKey: ['chat-counts'],
  queryFn: () =>
    gqlFetcher(chatCountsDoc, {}).then((data) => {
      const counts = (data as { chat?: { counts?: Partial<ChatCounts> | null } | null })?.chat
        ?.counts;
      return { ...EMPTY_COUNTS, ...counts } satisfies ChatCounts;
    }),
});

const chatMessagesQuery = `
  query ChatMessages($chatId: UUID!, $offset: Int, $limit: Int) {
    chat {
      byId(id: $chatId) {
        id
        kind
        sessionId
        postId
        gameId
        sessionStartedAt
        sessionExpiredAt
        postDeletedAt
        memberIds
        members {
          userId
          userData {
            id
            displayName
            avatar
          }
        }
        muted
        closedAt
        readOnly
        userId
        userData {
          id
          displayName
          avatar
          lastVisit
          blockState
          deleted
        }
      }
      messages(chatId: $chatId, offset: $offset, limit: $limit) {
        messages {
          id
          chatId
          senderId
          text
          clientMessageId
          createdAt
          updatedAt
          readByRecipient
          kind
          systemActor {
            userId
            userData {
              id
              displayName
            }
          }
          callOutcome
          callDurationSeconds
          callMediaType
          replyToMessageId
          replyTo {
            id
            senderId
            text
            createdAt
          }
          reactions {
            reaction
            count
          }
        }
        hasMore
        totalCount
      }
    }
  }
`;

type ChatMessagesQueryResult = {
  chat?: {
    byId?: {
      id?: string | null;
      kind?: string | null;
      sessionId?: string | null;
      postId?: string | null;
      gameId?: string | null;
      sessionStartedAt?: string | null;
      sessionExpiredAt?: string | null;
      postDeletedAt?: string | null;
      memberIds?: Array<string | null> | null;
      members?: Array<{
        userId?: string | null;
        userData?: {
          id?: string | null;
          displayName?: string | null;
          avatar?: string | null;
        } | null;
      } | null> | null;
      muted?: boolean | null;
      closedAt?: string | null;
      readOnly?: boolean | null;
      userId?: string | null;
      userData?: {
        id?: string | null;
        displayName?: string | null;
        avatar?: string | null;
        lastVisit?: string | null;
        blockState?: 'TEMPORARY' | 'PERMANENT' | null;
        deleted?: boolean | null;
      } | null;
    } | null;
    messages?: {
      messages?: Array<{
        id?: string | null;
        chatId?: string | null;
        senderId?: string | null;
        text?: string | null;
        clientMessageId?: string | null;
        createdAt?: string | null;
        updatedAt?: string | null;
        readByRecipient?: boolean | null;
        kind?: string | null;
        systemActor?: {
          userId?: string | null;
          userData?: {
            id?: string | null;
            displayName?: string | null;
          } | null;
        } | null;
        callOutcome?: string | null;
        callDurationSeconds?: number | null;
        callMediaType?: string | null;
        replyToMessageId?: string | null;
        replyTo?: {
          id?: string | null;
          senderId?: string | null;
          text?: string | null;
          createdAt?: string | null;
        } | null;
        reactions?: Array<{
          reaction?: string | null;
          count?: number | null;
        } | null> | null;
      } | null> | null;
      hasMore?: boolean | null;
      totalCount?: number | null;
    } | null;
  } | null;
};

export const chatMessagesQueryOptions = (input: {
  chatId: string;
  offset: number;
  limit: number;
}) => ({
  queryKey: ['chatMessages', input.chatId, input.limit, input.offset],
  queryFn: () =>
    gqlFetcher<ChatMessagesQueryResult, typeof input>(chatMessagesQuery, input).then((data) => ({
      chat: data?.chat?.byId ?? null,
      messages:
        data?.chat?.messages?.messages
          ?.filter(isDefined)
          .map((message) => ({
            id: message.id ?? '',
            chatId: message.chatId ?? input.chatId,
            senderId: message.senderId ?? '',
            text: message.text ?? '',
            // Ключ отправки: по нему своя неудачная строка узнаёт, что сообщение на самом деле
            // дошло (см. useOutgoingMessages).
            clientMessageId: message.clientMessageId ?? null,
            createdAt: message.createdAt ?? '',
            updatedAt: message.updatedAt ?? message.createdAt ?? '',
            readByRecipient: Boolean(message.readByRecipient),
            kind: message.kind ?? 'TEXT',
            systemActorName: message.systemActor?.userData?.displayName ?? null,
            // Id того, о ком строка: по нему чип ведёт в профиль (бриф приглашений §4.2).
            systemActorId: message.systemActor?.userId ?? message.systemActor?.userData?.id ?? null,
            callOutcome: message.callOutcome ?? null,
            callDurationSeconds: message.callDurationSeconds ?? null,
            callMediaType: message.callMediaType ?? null,
            replyToMessageId: message.replyToMessageId ?? null,
            replyTo: message.replyTo?.id
              ? {
                  id: message.replyTo.id,
                  senderId: message.replyTo.senderId ?? '',
                  text: message.replyTo.text ?? '',
                  createdAt: message.replyTo.createdAt ?? '',
                }
              : null,
            reactions:
              message.reactions
                ?.filter(isDefined)
                .map((reaction) => ({
                  reaction: reaction.reaction ?? '',
                  count: Number(reaction.count ?? 0),
                }))
                .filter((reaction) => reaction.reaction.length > 0) ?? [],
          }))
          /*
            У служебной строки автора нет намеренно, и условие «есть отправитель» выбрасывало её
            вместе с битыми записями: сперва так до ленты не доезжало «сессия началась», потом
            ровно так же пропала строка об уходе за пределы площадки.

            Поэтому проверка спрашивает про любой служебный вид, а не перечисляет сессионные:
            каждый новый вид, который заводит сервер, иначе молча исчезает здесь, и выглядит это
            как «бэкенд не пишет строку», хотя строка в базе есть.
          */
          .filter(
            (message) =>
              message.id.length > 0 &&
              (message.senderId.length > 0 ||
                isSystemKind(message.kind) ||
                isOffPlatformNoticeKind(message.kind))
          ) ?? [],
      hasMore: data?.chat?.messages?.hasMore ?? false,
      totalCount: data?.chat?.messages?.totalCount ?? 0,
    })),
});

const startChatDoc = graphql(`
  mutation StartChat($peerUserId: UUID!) {
    chat {
      start(input: { peerUserId: $peerUserId }) {
        __typename
        ... on Chat {
          id
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`);

export const startChat = async (input: { peerUserId: string }) =>
  gqlFetcher(startChatDoc, input).then((data) => data?.chat?.start);

const chatByIdQuery = `
  query ChatById($id: UUID!) {
    chat {
      byId(id: $id) {
        id
      }
    }
  }
`;

type ChatByIdQueryResult = {
  chat?: {
    byId?: {
      id?: string | null;
    } | null;
  } | null;
};

export const getChatById = async (input: { id: string }) =>
  gqlFetcher<ChatByIdQueryResult, typeof input>(chatByIdQuery, input).then(
    (data) => data?.chat?.byId ?? null
  );

const chatByPeerQuery = `
  query ChatByPeer($peerUserId: UUID!) {
    chat {
      byPeerUserId(peerUserId: $peerUserId) {
        id
      }
    }
  }
`;

type ChatByPeerQueryResult = {
  chat?: {
    byPeerUserId?: {
      id?: string | null;
    } | null;
  } | null;
};

export const findChatByPeer = async (input: { peerUserId: string }) =>
  gqlFetcher<ChatByPeerQueryResult, typeof input>(chatByPeerQuery, input).then(
    (data) => data?.chat?.byPeerUserId
  );

const sendChatMessageMutation = `
  mutation SendChatMessage($input: ChatSendInput!) {
    chat {
      send(input: $input) {
        __typename
        ... on ChatMessage {
          id
          chatId
          senderId
          text
          clientMessageId
          createdAt
          updatedAt
          readByRecipient
          replyToMessageId
          replyTo {
            id
            senderId
            text
            createdAt
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

type SendChatMessageMutationResult = {
  chat?: {
    send?:
      | {
          __typename?: 'ChatMessage';
          id?: string | null;
          chatId?: string | null;
          senderId?: string | null;
          text?: string | null;
          clientMessageId?: string | null;
          createdAt?: string | null;
          updatedAt?: string | null;
          readByRecipient?: boolean | null;
          replyToMessageId?: string | null;
          replyTo?: {
            id?: string | null;
            senderId?: string | null;
            text?: string | null;
            createdAt?: string | null;
          } | null;
        }
      | {
          __typename?: 'GlobalError';
          errorCode?: string | null;
          errorMessage?: string | null;
        }
      | {
          __typename?: 'InputError';
          errors?: Array<{
            name?: string | null;
            messagesErrors?: Array<string | null> | null;
          } | null> | null;
        }
      | null;
  } | null;
};

export const sendChatMessage = async (input: {
  chatId?: string | null;
  peerUserId?: string | null;
  text: string;
  replyToMessageId?: string | null;
  /** Ключ отправки: повтор с тем же ключом сервер не записывает второй раз. */
  clientMessageId?: string | null;
}) =>
  gqlFetcher<SendChatMessageMutationResult, { input: typeof input }>(sendChatMessageMutation, {
    input,
  }).then((data) => {
    // Человек написал и ждёт ответа — лучший момент спросить про уведомления, см. pushPrompt.
    notePushWorthyAction();
    return data?.chat?.send;
  });

const sendChatTypingMutation = `
  mutation SendChatTyping($chatId: UUID!, $typing: Boolean) {
    chat {
      typing(chatId: $chatId, typing: $typing) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`;

type SendChatTypingMutationResult = {
  chat?: {
    typing?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
    } | null;
  } | null;
};

export const sendChatTyping = async (input: { chatId: string; typing?: boolean }) =>
  gqlFetcher<SendChatTypingMutationResult, typeof input>(sendChatTypingMutation, input).then(
    (data) => data?.chat?.typing
  );

const reactChatMessageMutation = `
  mutation ReactChatMessage($input: ChatMessageReactionInput!) {
    chat {
      reactMessage(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

const unreactChatMessageMutation = `
  mutation UnreactChatMessage($input: ChatMessageReactionInput!) {
    chat {
      unreactMessage(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

type ChatReactMutationResult = {
  chat?: {
    reactMessage?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      errors?: Array<{
        name?: string | null;
        messagesErrors?: Array<string | null> | null;
      } | null> | null;
    } | null;
  } | null;
};

type ChatUnreactMutationResult = {
  chat?: {
    unreactMessage?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      errors?: Array<{
        name?: string | null;
        messagesErrors?: Array<string | null> | null;
      } | null> | null;
    } | null;
  } | null;
};

export const reactChatMessage = async (input: {
  chatId: string;
  messageId: string;
  reaction: string;
}) =>
  gqlFetcher<ChatReactMutationResult, { input: typeof input }>(reactChatMessageMutation, {
    input,
  }).then((data) => data?.chat?.reactMessage);

export const unreactChatMessage = async (input: {
  chatId: string;
  messageId: string;
  reaction: string;
}) =>
  gqlFetcher<ChatUnreactMutationResult, { input: typeof input }>(unreactChatMessageMutation, {
    input,
  }).then((data) => data?.chat?.unreactMessage);

/*
  Автор убирает своё сообщение.

  Не то же, что удаление переписки: строка остаётся на месте и становится надгробием — у обоих
  собеседников вместо текста «Сообщение удалено». Иначе чужая цитата указывала бы в пустоту.
*/
const deleteChatMessageMutation = `
  mutation DeleteChatMessage($chatId: UUID!, $messageId: UUID!) {
    chat {
      deleteMessage(chatId: $chatId, messageId: $messageId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

type DeleteChatMessageMutationResult = {
  chat?: {
    deleteMessage?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      errors?: Array<{
        name?: string | null;
        messagesErrors?: Array<string | null> | null;
      } | null> | null;
    } | null;
  } | null;
};

export const deleteChatMessage = async (input: { chatId: string; messageId: string }) =>
  gqlFetcher<DeleteChatMessageMutationResult, typeof input>(deleteChatMessageMutation, input).then(
    (data) => data?.chat?.deleteMessage
  );

const deleteChatMutation = `
  mutation DeleteChat($chatId: UUID!) {
    chat {
      delete(chatId: $chatId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

type DeleteChatMutationResult = {
  chat?: {
    delete?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      errors?: Array<{
        name?: string | null;
        messagesErrors?: Array<string | null> | null;
      } | null> | null;
    } | null;
  } | null;
};

export const deleteChat = async (input: { chatId: string }) =>
  gqlFetcher<DeleteChatMutationResult, typeof input>(deleteChatMutation, input).then(
    (data) => data?.chat?.delete
  );

/*
  Уйти из переписки сессии — у себя одного.

  Не то же, что удаление: то снимает переписку у всей команды и для сессии сервером запрещено.
  Понадобилось вместе с правилом «переписка переживает удалённую заявку» (20.09.2026): у неё
  больше нет принудительного конца, значит нужна дверь наружу.
*/
const leaveChatMutation = `
  mutation LeaveChat($chatId: UUID!) {
    chat {
      leave(chatId: $chatId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`;

type LeaveChatMutationResult = {
  chat?: {
    leave?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
      /* Запрос это поле уже спрашивал, а тип о нём не знал - разобрать `InputError` было нечем. */
      errors?: Array<{
        name?: string | null;
        messagesErrors?: Array<string | null> | null;
      } | null> | null;
    } | null;
  } | null;
};

export const leaveChat = async (input: { chatId: string }) =>
  gqlFetcher<LeaveChatMutationResult, typeof input>(leaveChatMutation, input).then(
    (data) => data?.chat?.leave
  );

const muteChatMutation = `
  mutation MuteChat($chatId: UUID!, $muted: Boolean) {
    chat {
      mute(chatId: $chatId, muted: $muted) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`;

type MuteChatMutationResult = {
  chat?: {
    mute?: {
      __typename?: string | null;
      status?: string | null;
      errorCode?: string | null;
      errorMessage?: string | null;
    } | null;
  } | null;
};

/**
 * Заглушение переписки. Серверное, в отличие от прежнего: то жило в этом браузере и гасило
 * только всплывающее сообщение и звук, а пуш и письмо уходили всё равно.
 */
export const muteChat = async (input: { chatId: string; muted: boolean }) =>
  gqlFetcher<MuteChatMutationResult, typeof input>(muteChatMutation, input).then(
    (data) => data?.chat?.mute
  );

const markChatReadDoc = graphql(`
  mutation MarkChatRead($chatId: UUID!, $upToMessageId: UUID) {
    chat {
      markRead(chatId: $chatId, upToMessageId: $upToMessageId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const markChatRead = async (input: { chatId: string; upToMessageId?: string | null }) =>
  gqlFetcher(markChatReadDoc, input).then((data) => data?.chat?.markRead);

const chatBlockStatusDoc = graphql(`
  query ChatBlockStatus($peerUserId: UUID!) {
    chat {
      blockStatus(peerUserId: $peerUserId) {
        blocked
        blockedByMe
        blockedByPeer
      }
    }
  }
`);

export const chatBlockStatusQueryOptions = (input: { peerUserId: string }) => ({
  queryKey: ['chatBlockStatus', input.peerUserId],
  queryFn: () =>
    gqlFetcher(chatBlockStatusDoc, input).then((data) => ({
      blocked: Boolean(data?.chat?.blockStatus?.blocked),
      blockedByMe: Boolean(data?.chat?.blockStatus?.blockedByMe),
      blockedByPeer: Boolean(data?.chat?.blockStatus?.blockedByPeer),
    })),
});

const blockChatUserDoc = graphql(`
  mutation BlockChatUser($peerUserId: UUID!) {
    chat {
      block(peerUserId: $peerUserId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`);

export const blockChatUser = async (input: { peerUserId: string }) =>
  gqlFetcher(blockChatUserDoc, input).then((data) => data?.chat?.block);

const unblockChatUserDoc = graphql(`
  mutation UnblockChatUser($peerUserId: UUID!) {
    chat {
      unblock(peerUserId: $peerUserId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            name
            messagesErrors
          }
        }
      }
    }
  }
`);

export const unblockChatUser = async (input: { peerUserId: string }) =>
  gqlFetcher(unblockChatUserDoc, input).then((data) => data?.chat?.unblock);

const hasUnreadChatsDoc = graphql(`
  query HasUnreadChats {
    chat {
      existUnread
    }
  }
`);

export const hasUnreadChatsQueryOptions = () => ({
  queryKey: ['chatsUnreadExist'],
  queryFn: () => gqlFetcher(hasUnreadChatsDoc).then((data) => Boolean(data?.chat?.existUnread)),
});

const unreadChatsCountDoc = graphql(`
  query UnreadChatsCount {
    chat {
      unreadCount
    }
  }
`);

const mutedChatsDoc = graphql(`
  query MutedChats {
    chat {
      mutedChatIds
    }
  }
`);

/**
 * Все заглушённые переписки аккаунта одним запросом - зеркало для всплывашек.
 *
 * Спрашивается один раз за сессию: решение «показывать всплывашку или нет» принимается в
 * обработчике сокета, где до кеша запросов не дотянуться, поэтому заглушённые держатся в сторе.
 * Наполнялся он только списком чатов и открытой перепиской, и на свежем устройстве, где ни то ни
 * другое ещё не открывали, был пуст - первая всплывашка из заглушённой переписки проскакивала.
 */
export const mutedChatsQueryOptions = () => ({
  queryKey: ['chatsMuted'],
  queryFn: () =>
    gqlFetcher(mutedChatsDoc).then((data) => (data?.chat?.mutedChatIds ?? []).filter(isDefined)),
  staleTime: Infinity,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: false as const,
});

export const unreadChatsCountQueryOptions = () => ({
  queryKey: ['chatsUnreadCount'],
  queryFn: () =>
    gqlFetcher(unreadChatsCountDoc).then((data) => Number(data?.chat?.unreadCount ?? 0)),
});
